import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';

const LookupPage = () => {
    const [scammers, setScammers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lookupQuery, setLookupQuery] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    const query = new URLSearchParams(location.search).get('query');

    useEffect(() => {
        if (query) {
            setLookupQuery(query);
            setLoading(true);
            fetch(`https://block.lenny.ie/scammers?query=${encodeURIComponent(query)}`)
                .then((response) => response.json())
                .then((data) => {
                    if (data.message) {
                        setScammers([]);
                        setError(data.message);
                    } else {
                        setScammers(data);
                        setError(null);
                    }
                    setLoading(false);
                })
                .catch((err) => {
                    setError('Failed to fetch data');
                    setLoading(false);
                });
        } else {
            setLoading(false);
            setError('No query provided');
        }
    }, [query]);

    const handleLookup = () => {
        if (lookupQuery.trim() !== '') {
            navigate(`/lookup?query=${encodeURIComponent(lookupQuery)}`);
        }
    };

    const handleInputChange = (event) => {
        setLookupQuery(event.target.value);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && lookupQuery.trim() !== '') {
            handleLookup();
        }
    };

    const processPreviousTags = (tags) => {
        const tagCount = tags.reduce((acc, tag) => {
            acc[tag] = (acc[tag] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(tagCount).map(([tag, count]) =>
            count > 1 ? `${tag} (${count} times)` : tag
        );
    };

    return (
        <div className="flex flex-col min-h-screen bg-background text-text">
            <Header />
            <div className="flex-grow">
                <section className="px-6 py-12 text-center">
                    <h2 className="text-4xl font-bold text-primary mb-8">Scammer Lookup</h2>
                    <div className="flex justify-center space-x-4 mb-6">
                        <input
                            id="scammer-lookup-input"
                            type="text"
                            placeholder="Enter Scammer Name or Discord ID"
                            className="px-4 py-2 border border-secondary rounded w-full max-w-md bg-secondary text-text placeholder-text focus:outline-none focus:ring-2 focus:ring-primary"
                            value={lookupQuery}
                            onChange={handleInputChange}
                            onKeyPress={handleKeyPress}
                        />
                        <button
                            className="px-6 py-2 bg-primary text-text rounded hover:bg-opacity-90 transition-all ease-in-out duration-300"
                            onClick={handleLookup}
                            disabled={lookupQuery.trim() === ''}
                        >
                            Lookup
                        </button>
                    </div>
                    <button
                        className="px-6 py-2 bg-secondary text-text rounded hover:bg-opacity-90 transition-all ease-in-out duration-300 mb-8"
                        onClick={() => navigate('/')}
                    >
                        Back to Homepage
                    </button>
                    {loading ? (
                        <div className="flex justify-center items-center">
                            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-primary"></div>
                        </div>
                    ) : scammers.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                            {scammers.map((scammer, index) => (
                                <div key={index} className="p-4 bg-secondary rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
                                    <h4 className="text-lg font-semibold text-accent mb-2">{scammer.tag}</h4>
                                    <p className="text-sm text-text"><strong>ID:</strong> {scammer.id}</p>
                                    <p className="text-sm text-text"><strong>Method:</strong> {scammer.method}</p>
                                    {scammer.previous_tags && scammer.previous_tags.length > 0 && (
                                        <p className="text-sm text-text"><strong>Previous Tags:</strong> {processPreviousTags(scammer.previous_tags).join(', ')}</p>
                                    )}
                                    {scammer.scammed && (
                                        <p className="text-sm text-text"><strong>Scammed:</strong> {scammer.scammed}</p>
                                    )}
                                    {scammer.amount && (
                                        <p className="text-sm text-text"><strong>Amount:</strong> {scammer.amount}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        error && (
                            <div className="text-center mt-4">
                                <p className="mb-2">{error}</p>
                                {error === "No scammers found matching the query" && (
                                    <>
                                        <p className="mb-2">If you think this is a mistake, feel free to join our Discord server and make a ticket:</p>
                                        <a
                                            href="https://discord.gg/scammerlist"
                                            className="text-primary underline"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Join Discord Server
                                        </a>
                                    </>
                                )}
                            </div>
                        )
                    )}
                </section>
            </div>
            <Footer />
        </div>
    );
};

export default LookupPage;
